/**
 * What a month's plan and fact are made of (decisions-log V306, V307): the covered part of the
 * month and why it starts or ends inside it, the calendar's days, the plan, and what the fact loses
 * — the person's leaves, the project's days off and the hours not worked. «План проектов» and
 * «План выплат» show it when a row is clicked and edit the fact in the «Зафиксировать» dialog.
 */
import { z } from 'zod';
import type { CalendarDayKind, WorkCalendarCode } from '../calendar';

/** Why the covered part starts after the 1st or ends before the last day of the month. */
export type PlanBoundReason = 'project' | 'person';

export interface PlanBound {
  readonly date: string;
  readonly reason: PlanBoundReason;
}

/** A day of the project's calendar inside the covered part: a holiday, or a weekend day worked. */
export interface PlanCalendarDay {
  readonly date: string;
  readonly kind: CalendarDayKind;
  readonly name: string;
}

export type PlanLeaveKind = 'vacation' | 'unpaid';

/** A leave of the person on the assignment this month: its workdays inside the covered part as hours. */
export interface PlanLeave {
  readonly leaveId: string;
  readonly kind: PlanLeaveKind;
  readonly startsOn: string;
  readonly endsOn: string;
  readonly workdays: number;
  readonly minutes: number;
  /** A leave on projects alone can be removed here; one with a salary event is removed with the event. */
  readonly canRemove: boolean;
}

/** The plan and fact of one assignment month. */
export interface PlanDetail {
  readonly calendar: WorkCalendarCode;
  /** The covered part of the month. */
  readonly from: string;
  readonly to: string;
  readonly startsOn: PlanBound | null;
  readonly endsOn: PlanBound | null;
  readonly monthWorkdays: number;
  readonly workdays: number;
  readonly days: readonly PlanCalendarDay[];
  /** The monthly plan the month's share is taken from: the assignment's, or 8 h per workday of the month (V221). */
  readonly monthlyPlanMinutes: number;
  /** The most the month can hold: the monthly plan × covered workdays ÷ the month's workdays. */
  readonly planMinutes: number;
  readonly leaves: readonly PlanLeave[];
  /** «Не отработано, ч» of the assignment month; null when none. */
  readonly offMinutes: number | null;
  /** The plan less the leaves and the hours off, not below 0: the fact until fixed (the project's days off are the project's, V310). */
  readonly expectedMinutes: number;
  /** The entered fact; null = the expected one. */
  readonly minutes: number | null;
  readonly factMinutes: number;
}

export interface TeamPlanLine {
  readonly assignmentId: string;
  readonly personId: string;
  readonly personName: string;
  readonly detail: PlanDetail;
}

/** The plan and fact of a project month billed by the hour: the team's lines added up. */
export interface ProjectPlanDetail {
  readonly calendar: WorkCalendarCode;
  readonly from: string;
  readonly to: string;
  readonly startsOn: PlanBound | null;
  readonly endsOn: PlanBound | null;
  readonly monthWorkdays: number;
  readonly workdays: number;
  readonly days: readonly PlanCalendarDay[];
  readonly team: readonly TeamPlanLine[];
  /** «Позиций» of the project (V309); null = the plan follows the team. */
  readonly positions: number | null;
  /** 8 h per covered workday: the plan of one position. */
  readonly positionPlanMinutes: number;
  /** Positions × the position plan (V309); else Σ of the team's plans, or one position's plan when nobody is assigned (V20). */
  readonly planMinutes: number;
  /** «Выходные на проекте, ч» per position (V310); null when none. */
  readonly projectOffMinutes: number | null;
  /**
   * Positions × (the position plan less the days off) (V310); without positions, Σ of the team's facts
   * each less the days off, or one position's plan less them when nobody is assigned.
   */
  readonly expectedMinutes: number;
  readonly minutes: number | null;
  readonly factMinutes: number;
}

/** The fact a month expects: the plan less what was not worked, never below 0. */
export const expectedMinutesOf = (planMinutes: number, absentMinutes: number): number => Math.max(0, planMinutes - absentMinutes);

/** Hours a month can lose are capped at 744 h (31 × 24). */
export const OFF_MAX_MINUTES = 744 * 60;
const offMinutes = z.number().int().min(0).max(OFF_MAX_MINUTES);

/**
 * The body of «Зафиксировать»: the hours off to write with the approval; left out, the month keeps
 * what it has. The fact itself is never typed (V309): the approval fixes the expected one.
 */
export const planFixSchema = z
  .object({
    offMinutes: offMinutes.nullable().optional(),
  })
  .strict();
export type PlanFix = z.infer<typeof planFixSchema>;

/** One line of a month's history (V308): a change of the record or of a leave on it, who made it. */
export interface PlanHistoryEntry {
  readonly id: string;
  readonly at: string;
  readonly summary: string;
  /** The status the change set (design `HST`); null for a plain write. */
  readonly label: string | null;
  /** Who made it (online); null locally and for system work. */
  readonly by: string | null;
}

/** What differs from the last approval: «Факт, ч: 152 → 160», «Отпуск добавлен: 2 ноя – 6 ноя». */
export interface PlanChange {
  readonly label: string;
  readonly from: string | null;
  readonly to: string | null;
}

/** GET …/history: the last approval still in force, what changed since it, every change of the month. */
export interface PlanHistory {
  readonly approved: { readonly at: string; readonly by: string | null } | null;
  readonly changes: readonly PlanChange[];
  readonly entries: readonly PlanHistoryEntry[];
}
